import type { ContractPeriod, DegreeDays, DjuSettings, EnergyKey, LngLat, PeriodKey, PlanBuilding, RatioSet, RatioTable, UsageKey, WeatherStation } from './types';
import { distanceKm } from './geo';

// ─── Référentiels (VALEURS DE DÉMONSTRATION — à fournir par le métier) ─────────

export const USAGES: { key: UsageKey; label: string }[] = [
  { key: 'resCollectif', label: 'Résidentiel collectif' },
  { key: 'resIndividuel', label: 'Résidentiel individuel' },
  { key: 'tertiaire', label: 'Tertiaire' },
  { key: 'enseignement', label: 'Enseignement' },
  { key: 'sante', label: 'Santé' },
  { key: 'autre', label: 'Autre' },
];

export const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'avant1948', label: '< 1948' },
  { key: 'p1948_1974', label: '1948-1974' },
  { key: 'p1975_1999', label: '1975-1999' },
  { key: 'p2000_2012', label: '2000-2012' },
  { key: 'apres2012', label: '> 2012' },
];

export const USAGE_LABEL = Object.fromEntries(USAGES.map((u) => [u.key, u.label])) as Record<UsageKey, string>;
export const PERIOD_LABEL = Object.fromEntries(PERIODS.map((p) => [p.key, p.label])) as Record<PeriodKey, string>;

/** Période de construction de référence : porte le ratio saisi et le coefficient 100 %. */
export const REF_PERIOD: PeriodKey = 'avant1948';

/** Postes d'un ratio : chauffage, eau chaude sanitaire, froid. */
export const ENERGIES: { key: EnergyKey; label: string; short: string; icon: string }[] = [
  { key: 'chauffage', label: 'Chauffage', short: 'Chauf.', icon: 'local_fire_department' },
  { key: 'ecs', label: 'Eau chaude sanitaire', short: 'ECS', icon: 'water_drop' },
  { key: 'froid', label: 'Froid', short: 'Froid', icon: 'ac_unit' },
];

/** DJ de référence des ratios de base : les ratios par défaut y sont ramenés par station. */
export const REF_DJU = 2500;
export const REF_DJCLIM = 110;

/** Besoins de chauffage de référence (kWh/m²/an) — valeurs de démonstration. */
const BASE_CHAUFFAGE: Record<UsageKey, Record<PeriodKey, number>> = {
  resCollectif: { avant1948: 165, p1948_1974: 148, p1975_1999: 119, p2000_2012: 89, apres2012: 48 },
  resIndividuel: { avant1948: 195, p1948_1974: 176, p1975_1999: 142, p2000_2012: 103, apres2012: 53 },
  tertiaire: { avant1948: 190, p1948_1974: 173, p1975_1999: 146, p2000_2012: 114, apres2012: 70 },
  enseignement: { avant1948: 155, p1948_1974: 140, p1975_1999: 118, p2000_2012: 93, apres2012: 56 },
  sante: { avant1948: 215, p1948_1974: 196, p1975_1999: 169, p2000_2012: 137, apres2012: 90 },
  autre: { avant1948: 138, p1948_1974: 124, p1975_1999: 104, p2000_2012: 83, apres2012: 50 },
};

/** ECS : peu dépendante de la période de construction. */
const BASE_ECS: Record<UsageKey, number> = {
  resCollectif: 25, resIndividuel: 25, tertiaire: 10, enseignement: 8, sante: 45, autre: 8,
};

/** Froid : croissant avec la période de construction (taux d'équipement). */
const BASE_FROID: Record<UsageKey, number> = {
  resCollectif: 4, resIndividuel: 2, tertiaire: 20, enseignement: 9, sante: 28, autre: 6,
};
const FROID_FACTOR: Record<PeriodKey, number> = {
  avant1948: 0.6, p1948_1974: 0.7, p1975_1999: 1, p2000_2012: 1.3, apres2012: 1.6,
};

const buildRatios = (heat: number, cool: number): RatioTable =>
  Object.fromEntries(USAGES.map((u) => [
    u.key,
    Object.fromEntries(PERIODS.map((p) => [
      p.key,
      {
        chauffage: Math.round(BASE_CHAUFFAGE[u.key][p.key] * heat),
        ecs: BASE_ECS[u.key],
        froid: Math.round(BASE_FROID[u.key] * FROID_FACTOR[p.key] * cool),
      } satisfies RatioSet,
    ])),
  ])) as RatioTable;

/**
 * Ratios de référence (station aux DJ de référence) — valeurs de démonstration.
 * Le chauffage décroît d'une période à la suivante : les coefficients de performance
 * dérivés sont donc tous < 100 % et décroissants de gauche à droite.
 */
export const DEFAULT_RATIOS: RatioTable = buildRatios(1, 1);

/**
 * Ratios par défaut pour un couple de degrés-jours : chauffage au prorata des DJU,
 * froid au prorata des DJClim. FORMULE DE DÉMONSTRATION, à confirmer par le métier.
 */
export const ratiosForDegrees = (dju: number, djclim: number): RatioTable =>
  buildRatios(dju / REF_DJU, djclim / REF_DJCLIM);

export const ratiosForStation = (s: WeatherStation): RatioTable => ratiosForDegrees(s.dju, s.djclim);

export const ratioTotal = (r: RatioSet): number => r.chauffage + r.ecs + r.froid;

/** Ratio synthétique d'une typologie : période de construction de référence, poste par poste. */
export function usageRatio(t: RatioTable, u: UsageKey): RatioSet {
  return t[u][REF_PERIOD];
}

/** Fixe le ratio de la période de référence : les autres périodes suivent leur coefficient. */
export function setUsageRatio(t: RatioTable, u: UsageKey, e: EnergyKey, target: number): RatioTable {
  const cur = t[u][REF_PERIOD][e];
  const periods = Object.fromEntries(PERIODS.map((p) => {
    const v = cur > 0 ? (t[u][p.key][e] * target) / cur : target;
    return [p.key, { ...t[u][p.key], [e]: Math.round(v * 10) / 10 }];
  })) as RatioTable[UsageKey];
  return { ...t, [u]: periods };
}

/**
 * Calibre des ratios saisis pour un couple de degrés-jours sur un autre couple :
 * chauffage au prorata des DJU, froid au prorata des DJClim, ECS inchangée.
 * FORMULE DE DÉMONSTRATION, à confirmer par le métier.
 */
export function calibrateRatios(t: RatioTable, from: DegreeDays, to: DegreeDays): RatioTable {
  const kHeat = from.dju > 0 ? to.dju / from.dju : 1;
  const kCool = from.djclim > 0 ? to.djclim / from.djclim : 1;
  const r1 = (v: number) => Math.round(v * 10) / 10;
  return Object.fromEntries(USAGES.map((u) => [
    u.key,
    Object.fromEntries(PERIODS.map((p) => {
      const s = t[u.key][p.key];
      return [p.key, { chauffage: r1(s.chauffage * kHeat), ecs: s.ecs, froid: r1(s.froid * kCool) } satisfies RatioSet];
    })),
  ])) as RatioTable;
}

/** Stations météo : DJU base 18 °C et DJClim base 18/26 °C — valeurs de démonstration. */
export const STATIONS: WeatherStation[] = [
  { id: 'montsouris', name: 'Paris-Montsouris (75)', dju: 2210, djclim: 115, position: [2.3381, 48.8216], altitude: 75, normals: 'normales 1991-2020' },
  { id: 'orly', name: 'Orly (94)', dju: 2380, djclim: 105, position: [2.3844, 48.7233], altitude: 89, normals: 'normales 1991-2020' },
  { id: 'bourget', name: 'Le Bourget (93)', dju: 2340, djclim: 100, position: [2.4414, 48.9694], altitude: 52, normals: 'normales 1991-2020' },
  { id: 'villacoublay', name: 'Vélizy-Villacoublay (78)', dju: 2450, djclim: 90, position: [2.2014, 48.7744], altitude: 177, normals: 'normales 1991-2020' },
  { id: 'roissy', name: 'Roissy-CDG (95)', dju: 2420, djclim: 95, position: [2.5478, 49.0097], altitude: 108, normals: 'normales 1991-2020' },
  { id: 'melun', name: 'Melun-Villaroche (77)', dju: 2490, djclim: 92, position: [2.6797, 48.6133], altitude: 91, normals: 'normales 1991-2020' },
];

/** Rayon de la zone d'influence affichée autour d'une station (démonstration). */
export const STATION_RADIUS_KM = 8;

/** Station la plus proche d'un point : proposition par défaut du projet. */
export function nearestStation(point: LngLat | null, stations: WeatherStation[] = STATIONS): WeatherStation {
  if (!point) return stations[0];
  return stations.reduce((best, s) => (distanceKm(point, s.position) < distanceKm(point, best.position) ? s : best), stations[0]);
}

export const DEFAULT_DJU: DjuSettings = { stationId: 'montsouris', reference: 2500 };

export const RATIO_MIN = 0;
export const RATIO_MAX = 1000;
export const isValidRatio = (v: number) => Number.isFinite(v) && v > RATIO_MIN && v <= RATIO_MAX;
export const isValidDju = (v: number) => Number.isFinite(v) && v >= 500 && v <= 5000;
export const isValidDjclim = (v: number) => Number.isFinite(v) && v >= 10 && v <= 1000;

export const cloneRatios = (r: RatioTable): RatioTable =>
  Object.fromEntries(USAGES.map((u) => [
    u.key,
    Object.fromEntries(PERIODS.map((p) => [p.key, { ...r[u.key][p.key] }])),
  ])) as RatioTable;

/** Nombre de valeurs (usage × période × poste) qui s'écartent de la référence. */
export function countModifiedRatios(r: RatioTable, ref: RatioTable = DEFAULT_RATIOS): number {
  let n = 0;
  for (const u of USAGES) {
    for (const p of PERIODS) {
      for (const e of ENERGIES) if (r[u.key][p.key][e.key] !== ref[u.key][p.key][e.key]) n += 1;
    }
  }
  return n;
}

export function periodOf(year: number): PeriodKey {
  if (year < 1948) return 'avant1948';
  if (year <= 1974) return 'p1948_1974';
  if (year <= 1999) return 'p1975_1999';
  if (year <= 2012) return 'p2000_2012';
  return 'apres2012';
}

// ─── Calcul de consommation ───────────────────────────────────────────────

export function stationOf(dju: DjuSettings): WeatherStation {
  return STATIONS.find((s) => s.id === dju.stationId) ?? STATIONS[0];
}

/**
 * Coefficient de correction climatique. FORMULE NON CONFIRMÉE PAR LE MÉTIER :
 * hypothèse « DJU station / DJU de référence ». Toute évolution se fait ici uniquement.
 */
export function djuCoefficient(dju: DjuSettings): number {
  return dju.reference > 0 ? stationOf(dju).dju / dju.reference : 1;
}

export interface BuildingConsumption {
  ratio: number;
  /** MWh/an */
  raw: number;
  coefficient: number;
  /** MWh/an */
  corrected: number;
}

export function buildingConsumption(b: PlanBuilding, ratios: RatioTable, dju: DjuSettings): BuildingConsumption {
  const ratio = ratioTotal(ratios[b.usage][b.period]);
  const raw = (ratio * b.heatedArea) / 1000;
  const coefficient = djuCoefficient(dju);
  return { ratio, raw, coefficient, corrected: raw * coefficient };
}

/** Consommation globale corrigée du périmètre, en MWh/an. */
export function totalConsumption(buildings: PlanBuilding[], ratios: RatioTable, dju: DjuSettings): number {
  const k = djuCoefficient(dju);
  return buildings.reduce((s, b) => s + (ratioTotal(ratios[b.usage][b.period]) * b.heatedArea) / 1000, 0) * k;
}

/** Classes de la légende carte (MWh/an corrigés) — couleurs de jeu de données (hex autorisé). */
export const CONSUMPTION_CLASSES = [
  { max: 25, label: '< 25', color: '#fde6c8' },
  { max: 75, label: '25 – 75', color: '#f9bb7d' },
  { max: 200, label: '75 – 200', color: '#ef6b26' },
  { max: 500, label: '200 – 500', color: '#c2410c' },
  { max: Infinity, label: '≥ 500', color: '#7c2d12' },
] as const;

// ─── Géométrie ─────────────────────────────────────────────────────────────

export { boundsOf, centerOf, circlePolygon, closeRing, distanceKm, inPerimeter, pointInPolygon } from './geo';

// ─── Période de contrat ────────────────────────────────────────────────────

const MONTH_NAMES = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** `2027-04` → `avril 2027`. */
export function monthLabel(value: string): string {
  const [y, m] = value.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return '—';
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

const monthIndex = (value: string): number => {
  const [y, m] = value.split('-').map(Number);
  return Number.isFinite(y) && Number.isFinite(m) ? y * 12 + (m - 1) : NaN;
};

/** Durée du contrat en mois, bornes incluses. 0 si la période est invalide. */
export function contractMonths(c: ContractPeriod): number {
  const a = monthIndex(c.start);
  const b = monthIndex(c.end);
  return Number.isFinite(a) && Number.isFinite(b) && b >= a ? b - a + 1 : 0;
}

export const isValidContract = (c: ContractPeriod): boolean => contractMonths(c) > 0;

/** `avril 2027 → mars 2047 · 20 ans`. Les durées non entières restent en mois. */
export function contractLabel(c: ContractPeriod): string {
  const months = contractMonths(c);
  if (months === 0) return 'Période à définir';
  const years = months / 12;
  const duration = Number.isInteger(years) ? `${years} an${years > 1 ? 's' : ''}` : `${months} mois`;
  return `${monthLabel(c.start)} → ${monthLabel(c.end)} · ${duration}`;
}

/** Période proposée par défaut : début le mois prochain, 20 ans d'exploitation. */
export function defaultContract(from = new Date()): ContractPeriod {
  const start = new Date(from.getFullYear(), from.getMonth() + 1, 1);
  const end = new Date(start.getFullYear() + 20, start.getMonth() - 1, 1);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return { start: iso(start), end: iso(end) };
}

// ─── Formats ───────────────────────────────────────────────────────────────

export const fmt = (n: number, digits = 0) =>
  n.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits });
export const gwh = (mwh: number) => `${fmt(mwh / 1000, 1)} GWh/an`;
export const today = () => new Date().toLocaleDateString('fr-FR');
